import { useEffect, useRef, useState } from "react";
import { Sparkles, ArrowRight, ArrowLeft, Check, X, Loader2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import DraftWithAIButton from "@/components/ai/DraftWithAIButton";

/* StudioOnboardingWizard — first-run guided setup for new tenants.

   Shown when the studio has empty hero_eyebrow, contact_email, and about_body
   AND has not yet completed/dismissed the wizard
   (`onboarding_wizard_completed_at` unset). The Allure founder studio is
   always exempt (its data is backfilled by the server-side migration).

   Captures four short slices in 4 steps and PATCHes them in one shot at the
   end. Each step accepts skip; final "Finish" persists
   `onboarding_wizard_completed_at = now()` so it never reopens. */

const TOTAL_STEPS = 5;

export default function StudioOnboardingWizard({ initial, onClose }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    studio_name: initial?.studio_name || "",
    hero_eyebrow: initial?.hero_eyebrow || "",
    hero_image: initial?.hero_image || "",
    about_title: initial?.about_title || "",
    about_body: initial?.about_body || "",
    contact_email: initial?.contact_email || "",
    contact_phone: initial?.contact_phone || "",
    contact_location: initial?.contact_location || "",
    // Default ON: new studios opt in by default. Wizard is the right place to
    // give them the agency to opt out before their site goes live anywhere
    // public. They can also toggle from Branding tab later.
    show_in_lumetier_hub: initial?.show_in_lumetier_hub !== false,
  });

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  // ESC closes (skip — does NOT mark complete)
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function persistAndFinish() {
    setSaving(true);
    try {
      // Only PATCH the fields the wizard actually exposed for editing —
      // never send hero_title_line1/line2 because the UI doesn't let the
      // tenant change them, and pushing the defaults would clobber any
      // value the tenant set elsewhere in Site Settings.
      const payload = {
        studio_name: form.studio_name,
        hero_eyebrow: form.hero_eyebrow,
        hero_image: form.hero_image,
        about_title: form.about_title,
        about_body: form.about_body,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        contact_location: form.contact_location,
        show_in_lumetier_hub: form.show_in_lumetier_hub,
        onboarding_wizard_completed_at: new Date().toISOString(),
      };
      await api.patch("/admin/site", payload);
      toast.success("Studio setup complete. Welcome to Lumetier.");
      onClose?.({ completed: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save your studio details");
    } finally {
      setSaving(false);
    }
  }

  async function dontShowAgain() {
    setSaving(true);
    try {
      await api.patch("/admin/site", {
        onboarding_wizard_completed_at: new Date().toISOString(),
      });
      onClose?.({ completed: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save your preference");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { title: "Studio identity", subtitle: "Your name and the line above your headline.", el: <StepIdentity form={form} set={set} /> },
    { title: "Cover photograph", subtitle: "One image sets the tone for everything else.", el: <StepHero form={form} set={set} /> },
    { title: "About your studio", subtitle: "A short paragraph clients read after the hero.", el: <StepAbout form={form} set={set} /> },
    { title: "How to reach you", subtitle: "Where bookings, replies, and questions land.", el: <StepContact form={form} set={set} /> },
    { title: "Join the public showcase", subtitle: "Be featured on lumetiercreatives.com — a backlink + free editorial real estate.", el: <StepFeatureOnLumetier form={form} set={set} /> },
  ];
  const current = steps[step];
  const pct = Math.round(((step + 1) / TOTAL_STEPS) * 100);
  const isLast = step === TOTAL_STEPS - 1;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
      data-testid="onboarding-wizard"
      role="dialog"
      aria-labelledby="wizard-title"
      aria-modal="true"
    >
      <div className="relative w-full max-w-3xl bg-card border border-border shadow-2xl shadow-black/60 rounded-sm overflow-hidden">
        {/* Skip / close */}
        <button
          onClick={() => onClose?.()}
          className="absolute top-4 right-4 z-10 w-9 h-9 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
          aria-label="Skip onboarding"
          data-testid="wizard-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header band */}
        <div className="relative px-7 sm:px-10 pt-8 pb-6 border-b border-border bg-gradient-to-br from-[hsl(var(--gold))]/[0.06] via-card to-card">
          <div className="absolute -top-12 -right-12 w-48 h-48 gold-aura rounded-full pointer-events-none" />
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))] font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            Welcome to Lumetier
          </div>
          <h2 id="wizard-title" className="font-serif text-3xl sm:text-4xl font-light mt-3 leading-tight">
            Let&apos;s set up your studio.
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-lg">
            Four quick steps and your public site goes from blank to bookable. Skip any field — you can come back later in Site Settings.
          </p>

          {/* Progress */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-[3px] bg-border/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--gold))] transition-all duration-300"
                style={{ width: `${pct}%` }}
                data-testid="wizard-progress-bar"
              />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
              Step {step + 1} of {TOTAL_STEPS}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 sm:px-10 py-8 min-h-[340px]">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground font-medium mb-2">
            {current.subtitle}
          </div>
          <h3 className="font-serif text-2xl font-light mb-6" data-testid="wizard-step-title">{current.title}</h3>
          {current.el}
        </div>

        {/* Footer */}
        <div className="px-7 sm:px-10 py-5 border-t border-border flex items-center justify-between gap-3 bg-background/40">
          {step === 0 ? (
            <button
              type="button"
              onClick={dontShowAgain}
              disabled={saving}
              className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="wizard-dont-show"
            >
              Don&apos;t show again
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 h-10 text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onClose?.()}
              disabled={saving}
              className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="wizard-skip"
            >
              Skip for now
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={persistAndFinish}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 h-10 bg-[hsl(var(--gold))] text-stone-950 font-medium text-[11px] uppercase tracking-[0.22em] hover:bg-[hsl(var(--gold))]/90 disabled:opacity-60 transition-colors"
                data-testid="wizard-finish"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {saving ? "Saving" : "Finish & launch"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 h-10 bg-foreground text-background font-medium text-[11px] uppercase tracking-[0.22em] hover:bg-foreground/90 disabled:opacity-60 transition-colors"
                data-testid="wizard-next"
              >
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Step components ──────────────────────────────────────────────────── */

function Field({ label, hint, children, testid }) {
  return (
    <label className="block mb-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium mb-2">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground/70 mt-1.5">{hint}</div>}
    </label>
  );
}

const inputCls =
  "w-full bg-background border border-border px-3.5 h-11 text-sm rounded-sm focus:outline-none focus:border-[hsl(var(--gold))]/60 transition-colors placeholder:text-muted-foreground/50";

function StepIdentity({ form, set }) {
  return (
    <div>
      <Field
        label="Studio name"
        hint="Shown as the wordmark in your site's top nav and emails."
        testid="wizard-field-studio-name"
      >
        <input
          type="text"
          value={form.studio_name}
          onChange={(e) => set("studio_name", e.target.value)}
          placeholder="e.g. Maya Chen Photography"
          className={inputCls}
          data-testid="wizard-input-studio-name"
        />
      </Field>
      <Field
        label="Eyebrow / location"
        hint="The tiny line above your big headline. Often a city or specialty."
        testid="wizard-field-eyebrow"
      >
        <input
          type="text"
          value={form.hero_eyebrow}
          onChange={(e) => set("hero_eyebrow", e.target.value)}
          placeholder="e.g. Brooklyn · Editorial portraiture"
          className={inputCls}
          data-testid="wizard-input-eyebrow"
        />
      </Field>
    </div>
  );
}

function StepHero({ form, set }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image larger than 15 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image", data.url);
      toast.success("Hero uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div>
      <Field
        label="Cover photograph"
        hint="A single hero image — the first thing visitors see. Skip and add later if you want."
        testid="wizard-field-hero"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="relative shrink-0 w-full sm:w-56 h-36 border border-dashed border-border hover:border-[hsl(var(--gold))]/60 bg-background/50 group overflow-hidden flex items-center justify-center transition-colors"
            data-testid="wizard-hero-upload-btn"
          >
            {form.hero_image ? (
              <img
                src={form.hero_image}
                alt="Hero preview"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="text-center px-4">
                <ImageIcon className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" strokeWidth={1.5} />
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Click to upload</div>
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              </div>
            )}
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="wizard-hero-file" />
          <div className="flex-1 min-w-0 space-y-3">
            <div className="text-[11px] text-muted-foreground leading-relaxed">
              JPG or PNG, up to 15 MB. Landscape orientation works best — your image will fill the entire viewport on desktop.
            </div>
            {form.hero_image && (
              <button
                type="button"
                onClick={() => set("hero_image", "")}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-rose-400 transition-colors"
                data-testid="wizard-hero-clear"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 h-9 text-[10px] uppercase tracking-[0.22em] border border-border hover:border-foreground/60 disabled:opacity-50"
              data-testid="wizard-hero-upload-text-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : (form.hero_image ? "Replace" : "Upload")}
            </button>
          </div>
        </div>
      </Field>
    </div>
  );
}

function StepAbout({ form, set }) {
  return (
    <div>
      <Field
        label="About — heading"
        hint="One line that frames who you serve."
        testid="wizard-field-about-title"
      >
        <input
          type="text"
          value={form.about_title}
          onChange={(e) => set("about_title", e.target.value)}
          placeholder="e.g. Editorial portraits for brands and the moments in between."
          className={inputCls}
          data-testid="wizard-input-about-title"
        />
      </Field>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">
          About — paragraph
        </div>
        <DraftWithAIButton
          purpose="about"
          context={form.about_title ? `About heading: ${form.about_title}` : ""}
          onDraft={(text) => set("about_body", text)}
          testid="wizard-draft-about-ai"
        />
      </div>
      <textarea
        rows={4}
        value={form.about_body}
        onChange={(e) => set("about_body", e.target.value)}
        placeholder="Studio specialties, your approach to lighting and direction, what clients walk away with."
        className={`${inputCls} h-auto py-3 resize-none`}
        data-testid="wizard-input-about-body"
      />
      <div className="text-[11px] text-muted-foreground/70 mt-1.5">
        2&ndash;3 sentences. Your voice. Click <em>Draft with AI</em> for a starting point you can edit.
      </div>
    </div>
  );
}

function StepContact({ form, set }) {
  return (
    <div>
      <Field label="Contact email" testid="wizard-field-email">
        <input
          type="email"
          value={form.contact_email}
          onChange={(e) => set("contact_email", e.target.value)}
          placeholder="hello@yourstudio.com"
          className={inputCls}
          data-testid="wizard-input-email"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-x-5">
        <Field label="Phone" testid="wizard-field-phone">
          <input
            type="tel"
            value={form.contact_phone}
            onChange={(e) => set("contact_phone", e.target.value)}
            placeholder="+1 (555) 010-0000"
            className={inputCls}
            data-testid="wizard-input-phone"
          />
        </Field>
        <Field label="Location" testid="wizard-field-location">
          <input
            type="text"
            value={form.contact_location}
            onChange={(e) => set("contact_location", e.target.value)}
            placeholder="By appointment — Brooklyn, NY"
            className={inputCls}
            data-testid="wizard-input-location"
          />
        </Field>
      </div>
    </div>
  );
}

/* StepFeatureOnLumetier — opt-in toggle for the public Studios on Lumetier
   showcase on lumetiercreatives.com. Default state is ON; the tenant can
   opt out here or later from Site Settings → Branding. */
function StepFeatureOnLumetier({ form, set }) {
  const on = Boolean(form.show_in_lumetier_hub);
  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={() => set("show_in_lumetier_hub", !on)}
        aria-pressed={on}
        data-testid="wizard-feature-on-lumetier-toggle"
        className={`group relative w-full text-left px-6 py-5 border transition-all rounded-sm ${
          on
            ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10"
            : "border-border hover:border-border/80 bg-card/40"
        }`}
      >
        <div className="flex items-start justify-between gap-5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base,#C9A961)]">
              Studios on Lumetier showcase
            </div>
            <div className="mt-2 font-serif text-xl italic font-light leading-tight">
              {on ? "You're in." : "Stay private for now."}
            </div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">
              {on
                ? "Your studio will appear on lumetiercreatives.com as a featured card with a backlink to your public site. Most studios leave this on — it's free editorial real estate on a higher-PageRank site."
                : "Your studio won't appear in the public showcase. You can opt in any time from Site Settings → Branding."}
            </p>
          </div>
          {/* Pill toggle */}
          <span
            aria-hidden
            className={`relative inline-flex shrink-0 mt-1 h-7 w-12 rounded-full border transition-colors ${
              on ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]" : "border-border bg-muted"
            }`}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-background shadow transition-transform ${
                on ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </span>
        </div>
      </button>
      <div className="grid sm:grid-cols-2 gap-4 text-[11px] text-muted-foreground/80 leading-relaxed pt-2">
        <div className="flex items-start gap-2.5">
          <Check className="w-3.5 h-3.5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
          <span>SEO backlink from a high-PageRank marketing site to your portfolio.</span>
        </div>
        <div className="flex items-start gap-2.5">
          <Check className="w-3.5 h-3.5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
          <span>Free editorial card with your hero, name, location, and a CTA to your site.</span>
        </div>
        <div className="flex items-start gap-2.5">
          <Check className="w-3.5 h-3.5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
          <span>Social proof for visitors browsing the Lumetier hub.</span>
        </div>
        <div className="flex items-start gap-2.5">
          <Check className="w-3.5 h-3.5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
          <span>Toggle off any time — no commitment, no lock-in.</span>
        </div>
      </div>
    </div>
  );
}
